import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useSound } from "@/lib/SoundContext";
import { INTERIOR_SCENE, DOOR_SCENE } from "@/data/content";
import FridgeObject from "@/components/FridgeObject";
import { TapeLabel, WireShelf } from "@/components/Bits";

// condensation droplets on the cavity walls — [%left, %top, scale]
const DROPLETS = [
  [4, 30, 1], [94, 36, 0.8], [8, 56, 0.9], [90, 62, 1.1],
  [15, 79, 0.8], [85, 84, 0.9], [50, 27, 0.7], [70, 50, 0.75],
];

function Icicles() {
  return (
    <svg className="absolute top-0 inset-x-0 w-full h-9 z-[7] pointer-events-none" viewBox="0 0 560 36" preserveAspectRatio="none" aria-hidden="true">
      <path
        d="M0 0 h560 v7 q-8 1 -9 9 q-2 9 -7 9 q-5 0 -6 -8 q-1 -6 -8 -6 q-9 0 -10 12 q-1 9 -6 9 q-5 0 -6 -10 q-1 -8 -9 -7 q-9 1 -10 11 q-2 10 -7 8 q-5 -2 -5 -10 q0 -7 -8 -7 q-10 0 -11 13 q-1 8 -6 8 q-5 0 -6 -9 q-1 -8 -9 -8 q-9 0 -10 11 q-2 9 -6 7 q-5 -2 -5 -9 q0 -8 -9 -8 q-8 0 -9 10 q-2 9 -6 7 q-5 -2 -5 -8 q0 -8 -8 -8 q-9 0 -10 12 q-1 8 -6 6 q-5 -2 -5 -8 q0 -8 -8 -8 q-9 0 -10 11 q-2 9 -6 7 q-5 -2 -5 -8 q0 -8 -8 -8 q-9 0 -10 12 q-1 8 -6 6 q-5 -2 -5 -8 q0 -8 -8 -8 q-9 0 -10 11 q-2 9 -6 7 q-5 -2 -5 -8 q0 -8 -8 -8 q-9 0 -10 12 q-1 8 -6 6 q-5 -2 -5 -8 q0 -8 -8 -8 l-6 -2 v-4 z"
        fill="#EAF6FF"
        stroke="#B9DFF5"
        strokeWidth="2"
      />
    </svg>
  );
}

function Bulb() {
  return (
    <div className="absolute left-1/2 -translate-x-1/2 -top-1 z-[8] pointer-events-none flex flex-col items-center" data-testid="fridge-bulb">
      <div className="w-1.5 h-5 bg-[#5B7A94] border-x border-ink/40" />
      <div
        className="w-7 h-9 rounded-t-md rounded-b-full bg-[#FFF3C4] border-[3px] border-ink"
        style={{ boxShadow: "0 6px 34px 14px rgba(255,236,150,0.55)" }}
      />
    </div>
  );
}

// soft kitchen silhouette behind the fridge — suggested, not detailed
function KitchenSilhouette() {
  const c1 = "rgba(43,35,32,0.07)";
  const c2 = "rgba(43,35,32,0.12)";
  return (
    <svg className="fixed inset-0 w-full h-full pointer-events-none z-0" viewBox="0 0 1440 800" preserveAspectRatio="xMidYMid slice" aria-hidden="true" data-testid="kitchen-silhouette">
      <g fill={c1}>
        {/* tiled backsplash band */}
        <rect x="0" y="250" width="1440" height="220" fill="rgba(43,35,32,0.05)" />
        {Array.from({ length: 24 }, (_, i) => (
          <rect key={"v" + i} x={i * 62} y="250" width="3" height="220" />
        ))}
        {[320, 390].map((y) => (
          <rect key={"h" + y} x="0" y={y} width="1440" height="3" />
        ))}
      </g>
      <g fill={c2}>
        {/* left wall shelf with plates + jars */}
        <rect x="50" y="150" rx="10" width="240" height="16" />
        <circle cx="104" cy="130" r="26" />
        <circle cx="162" cy="128" r="30" />
        <rect x="205" y="106" width="42" height="42" rx="9" />
        {/* hanging pan + ladle, left */}
        <rect x="110" y="290" width="6" height="80" rx="3" />
        <ellipse cx="113" cy="392" r="24" />
        <rect x="180" y="290" width="6" height="64" rx="3" />
        <path d="M158 360 q28 36 50 0 l-6 40 q-19 14 -38 0 z" />
        {/* left counter + cabinets */}
        <rect x="20" y="520" width="300" height="20" rx="10" />
        <rect x="40" y="540" width="120" height="150" rx="12" />
        <rect x="180" y="540" width="120" height="150" rx="12" />
        <circle cx="150" cy="615" r="8" />
        <circle cx="190" cy="615" r="8" />
        {/* potted plant bottom left */}
        <rect x="330" y="640" width="52" height="46" rx="9" />
        <ellipse cx="356" cy="616" rx="42" ry="27" />
        {/* right window with curtains + panes */}
        <rect x="1180" y="100" width="210" height="240" rx="14" />
        <rect x="1180" y="100" width="210" height="34" rx="14" fill="rgba(43,35,32,0.16)" />
        <rect x="1276" y="134" width="9" height="206" />
        <rect x="1180" y="222" width="210" height="9" />
        {/* right shelf with jars */}
        <rect x="1150" y="430" rx="10" width="250" height="16" />
        <rect x="1172" y="368" width="38" height="58" rx="9" />
        <rect x="1224" y="380" width="32" height="46" rx="9" />
        <circle cx="1304" cy="398" r="28" />
        {/* stove silhouette bottom right */}
        <rect x="1180" y="570" width="230" height="190" rx="16" />
        <ellipse cx="1295" cy="570" rx="70" ry="12" />
        <circle cx="1225" cy="600" r="9" />
        <circle cx="1260" cy="600" r="9" />
      </g>
    </svg>
  );
}

function ZoneObjects({ items, route, entrance }) {
  const navigate = useNavigate();
  const open = (e) => {
    e.stopPropagation();
    navigate(route);
  };
  return (
    <>
      <div className="hidden md:block absolute inset-0">
        {items.map((o, i) => (
          <div
            key={i}
            className="absolute"
            style={{ left: `${o.x}%`, top: `${o.y}%`, transform: "translateY(-100%)" }}
          >
            <FridgeObject
              objectKey={o.key}
              size={72 * (o.s || 1)}
              rot={o.rot || 0}
              entrance={entrance}
              delay={0.15 + i * 0.05}
              onClick={open}
            />
          </div>
        ))}
      </div>
      <div className="md:hidden absolute inset-0 flex flex-wrap items-end content-end justify-center gap-x-2 gap-y-1 p-2 pt-9">
        {items.map((o, i) => (
          <FridgeObject
            key={i}
            objectKey={o.key}
            size={54 * (o.s || 1)}
            rot={o.rot || 0}
            entrance={entrance}
            delay={0.15 + i * 0.05}
            onClick={open}
          />
        ))}
      </div>
    </>
  );
}

function Zone({ id, testid, label, route, bgClass, className = "", labelPos = "left-3 bottom-2", children }) {
  const { play } = useSound();
  const navigate = useNavigate();
  const zoneSound = id === "freezer" ? "whoosh" : id === "drawer" ? "drawer" : "pop";
  return (
    <motion.section
      data-testid={testid}
      className={`absolute inset-x-0 zone overflow-hidden ${bgClass} ${className}`}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1, duration: 0.5 }}
      onClick={() => {
        play(zoneSound);
        navigate(route);
      }}
    >
      {children}
      <div className={`absolute ${labelPos} z-20 pointer-events-none flex flex-col items-start gap-1`}>
        <svg viewBox="0 0 40 34" className="w-8 -mb-1 ml-1 text-ink drop-shadow-[0_2px_0_rgba(255,255,255,0.6)]" aria-hidden="true">
          <path d="M4 4 q22 -2 26 18" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          <path d="M26 14 l6 8 l-9 -1" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <TapeLabel className="!text-[12px] sm:!text-sm !px-3.5 !py-1 !border-[2.5px] drop-shadow-[0_0_14px_rgba(255,240,150,0.95)]">{label}</TapeLabel>
        <span className="font-hand text-[10px] sm:text-xs text-white/90 bg-[#2B5E8A]/60 rounded-full px-2 border border-white/40">
          click to explore →
        </span>
      </div>
    </motion.section>
  );
}

function DoorRack({ rack, items }) {
  return (
    <div className="relative flex-1 mx-1.5" data-testid={`door-rack-${rack}`}>
      {items.map((o, i) => (
        <div key={o.key} className="absolute" style={{ left: `${o.x}%`, bottom: 10 }}>
          <FridgeObject
            objectKey={o.key}
            size={66 * (o.s || 1)}
            rot={o.rot || 0}
            entrance
            delay={0.3 + rack * 0.12 + i * 0.06}
          />
        </div>
      ))}
      <div className="door-rack-line absolute bottom-0 inset-x-0" />
    </div>
  );
}

export default function OpenFridge() {
  const { startHum, stopHum } = useSound();
  useEffect(() => {
    startHum();
    return () => stopHum();
  }, [startHum, stopHum]);

  return (
    <div className="min-h-screen flex items-start justify-center pt-24 pb-14 px-3">
      <KitchenSilhouette />
      <p className="absolute top-[4.5rem] left-1/2 -translate-x-1/2 z-20 font-hand font-bold text-lg sm:text-xl text-ink/75 text-center whitespace-nowrap" data-testid="interior-hint">
        click a section to explore — every shelf opens something ↓
      </p>
      <div className="relative flex items-stretch z-10" style={{ perspective: "1400px" }}>
        {/* cabinet — one glossy blue cavity */}
        <motion.div
          className="relative w-[min(700px,94vw)] h-[min(860px,82vh)] min-h-[600px] rounded-[24px] border-[5px] border-ink cavity-bg overflow-hidden shadow-[0_18px_0_rgba(43,35,32,0.18)]"
          initial={{ scale: 0.96, y: 14 }}
          animate={{ scale: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 160, damping: 16 }}
          data-testid="fridge-open-cabinet"
        >
          {/* condensation on the walls */}
          {DROPLETS.map(([l, t, s], i) => (
            <span key={i} className="droplet" style={{ left: `${l}%`, top: `${t}%`, transform: `scale(${s})` }} />
          ))}

          {/* dashed dividers — clear separation between the three sections */}
          <div className="absolute left-2 right-2 top-[23%] border-t-4 border-dashed border-white/95 z-[10] pointer-events-none drop-shadow-[0_2px_4px_rgba(20,50,80,0.55)]" />
          <div className="absolute left-2 right-2 top-[76%] border-t-4 border-dashed border-white/95 z-[10] pointer-events-none drop-shadow-[0_2px_4px_rgba(20,50,80,0.55)]" />

          {/* FREEZER — frosty top compartment, floor = wire rack divider */}
          <Zone id="freezer" testid="zone-freezer" label="THE FREEZER — COPYWRITING" route="/freezer"
            bgClass="top-0 h-[23%]" labelPos="left-2 top-2">
            <div className="absolute inset-0 frost-overlay z-[5]" />
            <Icicles />
            <ZoneObjects items={INTERIOR_SCENE.freezer} route="/freezer" entrance />
            <WireShelf className="absolute bottom-0 left-0 right-0 w-full h-[26px] z-[6]" />
          </Zone>

          {/* MAIN CAVITY — two wire rack shelves, hanging bulb */}
          <Zone id="fridge" testid="zone-fridge" label="THE GOOD STUFF — CREATIVE DIRECTION" route="/frankenstein"
            bgClass="top-[23%] h-[53%]">
            <Bulb />
            <WireShelf className="absolute left-1.5 top-[40%] h-[28px] w-[calc(100%-12px)] z-[5]" />
            <WireShelf className="absolute left-1.5 top-[85%] h-[28px] w-[calc(100%-12px)] z-[5]" />
            <ZoneObjects items={INTERIOR_SCENE.shelves} route="/frankenstein" entrance />
          </Zone>

          {/* CRISPER DRAWER — frosted bin with handle, produce glowing through */}
          <Zone id="drawer" testid="zone-drawer" label="FRUITS & VEGGIES — BRANDS WORKED WITH" route="/brand-building"
            bgClass="top-[76%] h-[24%] bg-[#8FD8C0]/25">
            <ZoneObjects items={INTERIOR_SCENE.drawer} route="/brand-building" entrance />
            <div className="crisper-front absolute inset-x-1.5 top-1 bottom-1.5 rounded-xl z-[6] pointer-events-none border-x-[3px] border-b-[3px] border-ink/70" />
            <div className="absolute left-1/2 -translate-x-1/2 top-3 w-24 h-3.5 rounded-full bg-[#EAF6FF]/90 border-[3px] border-ink z-[7] pointer-events-none" />
          </Zone>
        </motion.div>

        {/* open door on the RIGHT — interior liner with stocked racks (desktop) */}
        <motion.aside
          className="door-ajar hidden lg:flex flex-col self-stretch ml-1 pb-2"
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.25, duration: 0.5 }}
          data-testid="fridge-door-rack"
        >
          <span className="tape !text-[10px] rotate-6 self-center mt-2 mb-1">DOOR</span>
          {[0, 1, 2].map((rack) => (
            <DoorRack key={rack} rack={rack} items={DOOR_SCENE.filter((o) => o.rack === rack)} />
          ))}
        </motion.aside>

        {/* feet */}
        <div className="absolute -bottom-5 left-10 w-12 h-6 bg-[#8A9099] border-[3px] border-ink rounded-b-lg" />
        <div className="absolute -bottom-5 right-10 w-12 h-6 bg-[#8A9099] border-[3px] border-ink rounded-b-lg" />
        <div className="absolute -bottom-9 left-1/2 -translate-x-1/2 w-[85%] h-6 bg-ink/10 rounded-full blur-md" />
      </div>
    </div>
  );
}
